import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Stepper,
  Step,
  StepLabel,
  CircularProgress
} from '@mui/material';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5006/api';

const steps = ['Personal Information', 'Verify Email', 'Start Test'];

const RegistrationPage = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpError, setOtpError] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    registrationNumber: '',
    institution: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.fullName) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.registrationNumber) {
      newErrors.registrationNumber = 'Registration number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendOTP = async () => {
    if (!formData.email) {
      toast.error('Please enter your email first');
      return;
    }

    setIsLoading(true);
    setOtpError('');
    try {
      await axios.post(`${API_URL}/auth/send-otp-exam`, {
        email: formData.email
      });
      setOtpSent(true);
      toast.success('Verification code sent to your email!');
    } catch (error) {
      setOtpError(error.response?.data?.error || 'Failed to send OTP');
      toast.error('Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode.trim()) {
      setOtpError('Please enter the verification code');
      return;
    }

    setIsLoading(true);
    setOtpError('');
    try {
      const res = await axios.post(`${API_URL}/auth/verify-otp`, {
        email: formData.email,
        otpCode: otpCode.trim()
      });
      if (res.data.verified) {
        setEmailVerified(true);
        toast.success('Email verified!');
      }
    } catch (error) {
      setOtpError(error.response?.data?.error || 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      if (!validateForm()) return;
      setActiveStep(1);
    } else if (activeStep === 1) {
      if (!otpSent) {
        toast.error('Please request a verification code first');
        return;
      }
      if (!emailVerified) {
        toast.error('Please verify your email first');
        return;
      }
      setActiveStep(2);
    } else if (activeStep === 2) {
      setIsLoading(true);
      try {
        const response = await axios.post(`${API_URL}/test/start`, {
          email: formData.email,
          fullName: formData.fullName,
          registrationNumber: formData.registrationNumber,
          institution: formData.institution
        });

        toast.success('Test initialized successfully!');
        navigate('/test', { state: { testData: response.data } });
      } catch (error) {
        console.error('Error starting test:', error);
        if (error.response?.status === 400) {
          toast.error(error.response.data.error || 'Invalid data');
        } else {
          toast.error('Failed to start test. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Participant Registration
        </Typography>
        
        <Typography variant="subtitle1" gutterBottom align="center" sx={{ mb: 4, color: 'text.secondary' }}>
          Verify your identity to begin the assessment
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 1: Personal Information */}
        {activeStep === 0 && (
          <Box component="form" noValidate autoComplete="off">
            <Alert severity="info" sx={{ mb: 3 }}>
              Provide your details to register for the test. A verification code will be sent to your email.
            </Alert>

            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email || 'We will send a verification code to this address'}
              margin="normal"
              required
              disabled={isLoading}
              placeholder="you@example.com"
            />

            <TextField
              fullWidth
              label="Full Name"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              error={!!errors.fullName}
              helperText={errors.fullName || `${formData.fullName.length}/100 characters`}
              margin="normal"
              required
              disabled={isLoading}
              placeholder="Enter your full legal name"
              inputProps={{ maxLength: 100 }}
            />

            <TextField
              fullWidth
              label="Registration Number"
              name="registrationNumber"
              value={formData.registrationNumber}
              onChange={handleChange}
              error={!!errors.registrationNumber}
              helperText={errors.registrationNumber || `${formData.registrationNumber.length}/50 characters · Provided by your institution`}
              margin="normal"
              required
              disabled={isLoading}
              placeholder="e.g., 20240001"
              inputProps={{ maxLength: 50 }}
            />

            <TextField
              fullWidth
              label="Institution/Organization (Optional)"
              name="institution"
              value={formData.institution}
              onChange={handleChange}
              margin="normal"
              disabled={isLoading}
              placeholder="e.g., University of Rwanda"
              helperText={`${formData.institution.length}/150 characters`}
              inputProps={{ maxLength: 150 }}
            />

            <Alert severity="warning" sx={{ mt: 3, mb: 2 }}>
              <Box component="div">
                <Typography variant="body2" component="strong" display="block" gutterBottom>
                  Important:
                </Typography>
                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                  <Typography component="li" variant="body2">Questions are pulled from the question bank</Typography>
                  <Typography component="li" variant="body2">Time limits vary by exam configuration</Typography>
                  <Typography component="li" variant="body2">Must be completed in one sitting</Typography>
                </Box>
              </Box>
            </Alert>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="contained" onClick={handleNext} disabled={isLoading}>
                Next — Verify Email
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 2: OTP Verification */}
        {activeStep === 1 && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              A verification code will be sent to <strong>{formData.email}</strong>
            </Alert>

            {!otpSent && (
              <Button
                variant="contained"
                size="large"
                onClick={handleSendOTP}
                disabled={isLoading}
                sx={{ mb: 3 }}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Send Verification Code'}
              </Button>
            )}

            {otpSent && !emailVerified && (
              <>
                <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                  Enter the 6-digit code sent to your email
                </Typography>

                <TextField
                  fullWidth
                  label="Verification Code"
                  value={otpCode}
                  onChange={e => { setOtpCode(e.target.value); setOtpError(''); }}
                  error={!!otpError}
                  helperText={otpError}
                  sx={{ maxWidth: 300, mx: 'auto', mb: 2 }}
                  inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '8px' } }}
                  disabled={isLoading}
                />

                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleVerifyOTP}
                    disabled={isLoading || otpCode.length < 6}
                  >
                    {isLoading ? <CircularProgress size={24} /> : 'Verify Code'}
                  </Button>
                </Box>

                <Button
                  size="small"
                  onClick={handleSendOTP}
                  disabled={isLoading}
                >
                  Resend Code
                </Button>
              </>
            )}

            {emailVerified && (
              <Alert severity="success" sx={{ mb: 3 }}>
                ✅ Email verified successfully! You can now proceed to start the test.
              </Alert>
            )}
          </Box>
        )}

        {/* Step 3: Start Test */}
        {activeStep === 2 && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body1">
                ✅ Verified as <strong>{formData.fullName}</strong> ({formData.registrationNumber})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formData.email} — {formData.institution || 'No institution'}
              </Typography>
            </Alert>

            <Typography variant="h6" gutterBottom>
              Ready to begin the assessment?
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Time limits and question count are configured per exam • Auto-submit on timeout
            </Typography>

            <Button
              variant="contained"
              size="large"
              color="success"
              onClick={handleNext}
              disabled={isLoading}
              sx={{ px: 6, py: 1.5 }}
            >
              {isLoading ? 'Starting...' : 'Accept and Start'}
            </Button>
          </Box>
        )}

        {/* Navigation */}
        {activeStep > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button disabled={isLoading} onClick={handleBack}>
              Back
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default RegistrationPage;